import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Transaction } from "@/context/FinanceContext";
import { PersistedState, emptySeed } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";
import { mapSupabaseToLocalTransaction } from "@/utils/transactionMapper";
import { logger } from "@/lib/logger";
import { isPending } from "./pendingWrites";

interface FilterByAssignmentsFn {
  <T extends { id: string }>(items: T[], tableName: string): T[];
}

// ============ Chaves de cache / throttle ============
// OTIMIZAÇÃO EGRESS: evitar refetch completo a cada navegação/montagem
const LAST_FULL_SYNC_KEY = "finance-last-full-sync";
const LAST_TX_UPDATED_AT_KEY = "finance-last-tx-updated-at";
// Janela mínima entre dois full-syncs disparados automaticamente
const AUTO_FULL_SYNC_THROTTLE_MS = 5 * 60 * 1000; // 5 min
// Janela mínima entre dois forceRefresh manuais
const MANUAL_REFRESH_THROTTLE_MS = 15 * 1000; // 15 s

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

export function useSupabaseRealtimeSync(
  user: any,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  useSupabaseSync: any,
  filterByAssignments?: FilterByAssignmentsFn
) {
  const [syncing, setSyncing] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Use ref to always have the latest filterByAssignments
  const filterByAssignmentsRef = useRef(filterByAssignments);
  filterByAssignmentsRef.current = filterByAssignments;

  // Refs para throttle/guards
  const lastManualRefreshRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const channelRef = useRef<any>(null);
  const userIdRef = useRef<string | null>(null);

  const {
    fetchChartOfAccounts: fetchCategories,
    fetchAccounts,
    fetchCostCenters,
    fetchPaymentMethods,
    fetchSuppliers,
    fetchTransactions,
  } = useSupabaseSync;

  // ============ Sync principal ============
  // mode: 'full' baixa tudo. 'incremental' baixa apenas transações alteradas.
  const syncFromSupabase = useCallback(
    async (mode: "full" | "incremental" = "full") => {
      if (!user) return;
      if (inFlightRef.current) return; // evita corridas
      inFlightRef.current = true;
      setSyncing(true);

      // Retry helper: 1 retry com backoff curto para reduzir estados vazios por falha transitória
      const withRetry = async (fn: () => Promise<any>): Promise<any> => {
        try {
          return await fn();
        } catch (e) {
          await new Promise((r) => setTimeout(r, 800));
          return await fn();
        }
      };


      try {
        // Se estamos em incremental mas o estado local está vazio, promover para full
        // (evita ficar preso a um delta que não traz nada)
        let effectiveMode = mode;
        if (effectiveMode === "incremental") {
          let currentTxCount = 0;
          setState((s) => {
            currentTxCount = s.transactions.length;
            return s;
          });
          if (currentTxCount === 0) {
            effectiveMode = "full";
          }
        }
        const sinceIso =
          effectiveMode === "incremental"
            ? readStorage(LAST_TX_UPDATED_AT_KEY) || undefined
            : undefined;


        const fullDataPromise = effectiveMode === "full"
          ? Promise.all([
              withRetry(() => fetchCategories()),
              withRetry(() => fetchAccounts()),
              withRetry(() => fetchCostCenters()),
              withRetry(() => fetchPaymentMethods()),
              withRetry(() => fetchSuppliers()),
            ])
          : Promise.resolve([null, null, null, null, null]);
        const [fullData, supabaseTransactions] = await Promise.all([
          fullDataPromise,
          withRetry(() => fetchTransactions(sinceIso)),
        ]);
        const [
          supabaseCategories,
          supabaseAccountsRaw,
          supabaseCostCenters,
          supabasePaymentMethods,
          supabaseSuppliers,
        ] = fullData;


        const now = new Date();
        setLastSyncTime(now);
        writeStorage(LAST_FULL_SYNC_KEY, String(now.getTime()));

        // Atualiza marca d'água de updated_at (apenas se veio algo)
        if (supabaseTransactions && supabaseTransactions.length > 0) {
          const maxUpdatedAt = supabaseTransactions.reduce(
            (acc: string, t: any) =>
              t.updated_at && t.updated_at > acc ? t.updated_at : acc,
            readStorage(LAST_TX_UPDATED_AT_KEY) || ""
          );
          if (maxUpdatedAt) writeStorage(LAST_TX_UPDATED_AT_KEY, maxUpdatedAt);
        } else if (effectiveMode === "full") {
          // full sync sem registros — limpa marca
          writeStorage(LAST_TX_UPDATED_AT_KEY, "");
        }

        const supabaseAccounts = supabaseAccountsRaw;
        const currentFilter = filterByAssignmentsRef.current;

        setState((current) => {
          const categories = effectiveMode === "full" ? (supabaseCategories || []).map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            type: cat.type,
            dreCategory: cat.dre_category,
            hidden: cat.hidden,
            level2Id: cat.level2_id,
            code: cat.code,
          })) : current.categories;

          const rawAccounts = effectiveMode === "full" ? (supabaseAccounts || []).map((acc: any) => ({
            id: acc.id,
            name: acc.name,
            color: acc.color,
            initialBalance: acc.initial_balance,
            agencia: acc.agencia ?? undefined,
            contaCorrente: acc.conta_corrente ?? undefined,
          })) : current.accounts;

          const rawCostCenters = effectiveMode === "full" ? (supabaseCostCenters || []).map((cc: any) => ({
            id: cc.id,
            name: cc.name,
          })) : current.costCenters;

          const paymentMethods = effectiveMode === "full" ? (supabasePaymentMethods || []).map((pm: any) => ({
            id: pm.id,
            name: pm.name,
          })) : current.paymentMethods;

          const suppliers = effectiveMode === "full" ? (supabaseSuppliers || []).map((s: any) => ({
            id: s.id,
            name: s.name,
            cnpj: s.cnpj ?? undefined,
            anexo: s.anexo ?? undefined,
          })) : current.suppliers;

          const incomingTransactions = (supabaseTransactions || []).map(
            mapSupabaseToLocalTransaction
          );

          // Mescla incremental: substitui registros existentes, mantém o resto.
          // NUNCA sobrescrever com lista vazia em modo incremental.
          let transactions: Transaction[];
          if (effectiveMode === "incremental") {
            if (incomingTransactions.length === 0) {
              transactions = current.transactions;
            } else {
              const applicable = incomingTransactions.filter((t) => !isPending(t.id));
              const incomingIds = new Set(applicable.map((t) => t.id));
              const kept = current.transactions.filter((t) => !incomingIds.has(t.id));
              transactions = [...applicable, ...kept];
            }
          } else {
            // Full sync: preserva as linhas com gravação local em andamento
            const localById = new Map(current.transactions.map((t) => [t.id, t]));
            transactions = incomingTransactions.map((t) =>
              isPending(t.id) ? localById.get(t.id) ?? t : t
            );
            // Linhas pendentes ainda não visíveis no retorno do banco
            for (const t of current.transactions) {
              if (isPending(t.id) && !incomingTransactions.some((i) => i.id === t.id)) {
                transactions.unshift(t);
              }
            }
          }


          const accounts = effectiveMode === "full" && currentFilter
            ? currentFilter(rawAccounts, "accounts")
            : rawAccounts;

          const costCenters = effectiveMode === "full" && currentFilter
            ? currentFilter(rawCostCenters, "cost_centers")
            : rawCostCenters;

          return {
            categories,
            accounts,
            costCenters,
            paymentMethods,
            suppliers,
            transactions,
          };
        });
      } catch (error) {
        logger.error("Sync error");
        toast({
          variant: "destructive",
          title: "Erro na sincronização",
          description: "Não foi possível carregar os dados da nuvem.",
        });
      } finally {
        setSyncing(false);
        inFlightRef.current = false;
      }
    },
    [
      user,
      fetchCategories,
      fetchAccounts,
      fetchCostCenters,
      fetchPaymentMethods,
      fetchSuppliers,
      fetchTransactions,
      setState,
    ]
  );

  // ForceRefresh: por padrão respeita throttle; passe { force: true } para ignorar
  const forceRefresh = useCallback(async (opts?: { force?: boolean }) => {
    if (!user) return;
    const now = Date.now();
    if (!opts?.force && now - lastManualRefreshRef.current < MANUAL_REFRESH_THROTTLE_MS) {
      return;
    }
    lastManualRefreshRef.current = now;
    await syncFromSupabase("full");
  }, [user, syncFromSupabase]);

  // ============ Realtime ============
  // Único canal por usuário. Pausa quando a aba está oculta para reduzir
  // consumo de Realtime/egress, e reativa no visibilitychange.
  const setupRealtimeSync = useCallback(() => {
    if (!user) return;
    if (channelRef.current) return; // evita canal duplicado

    const channel = supabase
      .channel(`transactions-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "transactions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newRecord = payload.new as any;
          setLastSyncTime(new Date());

          if (payload.eventType === "INSERT") {
            const transaction = mapSupabaseToLocalTransaction(newRecord);
            if (newRecord?.updated_at) {
              writeStorage(LAST_TX_UPDATED_AT_KEY, newRecord.updated_at);
            }
            setState((current) => ({
              ...current,
              transactions: [
                transaction,
                ...current.transactions.filter((t) => t.id !== transaction.id),
              ],
            }));
          } else if (payload.eventType === "UPDATE") {
            const updatedTransaction = mapSupabaseToLocalTransaction(newRecord);
            if (newRecord?.updated_at) {
              writeStorage(LAST_TX_UPDATED_AT_KEY, newRecord.updated_at);
            }
            // Não sobrescrever linha com gravação local em andamento
            if (!isPending(updatedTransaction.id)) {
              setState((current) => ({
                ...current,
                transactions: current.transactions.map((t) =>
                  t.id === updatedTransaction.id ? updatedTransaction : t
                ),
              }));
            }
          } else if (payload.eventType === "DELETE") {
            const deletedId = (payload.old as any)?.id;
            if (deletedId) {
              // Avança a marca d'água para que o próximo delta não reprocese estado antigo
              writeStorage(LAST_TX_UPDATED_AT_KEY, new Date().toISOString());
              setState((current) => ({
                ...current,
                transactions: current.transactions.filter((t) => t.id !== deletedId),
              }));
            }
          }

        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          setRealtimeConnected(true);
          setLastSyncTime(new Date());
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          logger.error("Realtime channel error or timeout");
          setRealtimeConnected(false);
          // Tenta reconectar após um tempo se ainda houver usuário
          setTimeout(() => {
            if (userIdRef.current) {
              teardownChannel();
              setupRealtimeSync();
            }
          }, 5000);
        } else if (status === "CLOSED") {
          setRealtimeConnected(false);
        }
      });

    channelRef.current = channel;
  }, [user, setState]);

  const teardownChannel = useCallback(() => {
    if (channelRef.current) {
      try {
        supabase.removeChannel(channelRef.current);
      } catch {}
      channelRef.current = null;
      setRealtimeConnected(false);
    }
  }, []);

  // Setup Realtime + handlers de visibilidade / online / focus
  useEffect(() => {
    userIdRef.current = user?.id ?? null;

    if (!user) {
      teardownChannel();
      setRealtimeConnected(false);
      return;
    }

    const start = setTimeout(() => setupRealtimeSync(), 100);

    const triggerDelta = () => {
      if (!userIdRef.current) return;
      // Se o estado local está vazio, o próprio syncFromSupabase decidirá fazer full
      syncFromSupabase("incremental");
    };

    const handleVisibility = () => {
      if (document.hidden) {
        teardownChannel();
      } else {
        setupRealtimeSync();
        triggerDelta();
      }
    };

    const handleOnline = () => triggerDelta();
    const handleFocus = () => triggerDelta();

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearTimeout(start);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleFocus);
      teardownChannel();
    };
  }, [user, setupRealtimeSync, teardownChannel, syncFromSupabase]);

  // Sync inicial — só limpa cache quando user.id REALMENTE muda
  // (evita resetar estado em flickers momentâneos de auth)
  const lastInitUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const newUserId = user?.id ?? null;
    const prevUserId = lastInitUserIdRef.current;

    if (!user) {
      // Só limpa se antes havia um usuário diferente (logout real)
      if (prevUserId !== null) {
        lastInitUserIdRef.current = null;
        setState(emptySeed);
        setLastSyncTime(null);
        setRealtimeConnected(false);
        try {
          localStorage.removeItem("finance-store-v3");
          localStorage.removeItem(LAST_FULL_SYNC_KEY);
          localStorage.removeItem(LAST_TX_UPDATED_AT_KEY);
        } catch {}
      }
      return;
    }

    // Mesmo usuário — não re-hidratar nem re-sync (evita resets)
    if (prevUserId === newUserId) return;
    lastInitUserIdRef.current = newUserId;

    // Hidrata estado a partir do cache local imediatamente (UX) — apenas
    // se houver dados úteis no cache (evita "envenenamento" por cache vazio).
    try {
      const cached = localStorage.getItem("finance-store-v3");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (
          parsed &&
          typeof parsed === "object" &&
          Array.isArray(parsed.transactions) &&
          parsed.transactions.length > 0
        ) {
          setState((current) => ({ ...current, ...parsed }));
        }
      }
    } catch {}

    const lastFull = Number(readStorage(LAST_FULL_SYNC_KEY) || 0);
    const elapsed = Date.now() - lastFull;
    const hasTxWatermark = !!readStorage(LAST_TX_UPDATED_AT_KEY);

    const mode: "full" | "incremental" =
      lastFull > 0 && elapsed < AUTO_FULL_SYNC_THROTTLE_MS && hasTxWatermark
        ? "incremental"
        : "full";

    const timeoutId = setTimeout(() => {
      syncFromSupabase(mode);
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [user, setState, syncFromSupabase]);


  return {
    syncing,
    realtimeConnected,
    lastSyncTime,
    syncFromSupabase: () => syncFromSupabase("full"),
    forceRefresh,
  };
}
